<template>
    <div class="SidebarMenu">
        <div class="SidebarMenu__headerAndMenu">
            <div class="SidebarMenu__logoContainer">
                <slot name="logo" />
            </div>
            <div class="SidebarMenu__menuContainer">
                <SidebarMenuItem
                    v-for="item in flatMenuItems"
                    :key="item.name"
                    :active="activeMenu === item.name"
                    :href="item.href"
                    :icon="item.icon"
                    :label="item.label"
                    :notification-config="item.notificationConfig"
                    :processing="item.processing"
                    class="SidebarMenu__topLevelItem"
                />
                <RbacDisplayGuard :features="configMenuFeatures" required-any>
                    <SidebarMenuGroup
                        ref="configurationItem"
                        :icon="faGear"
                        label="Configure"
                        popover-class="SidebarMenu__popover SidebarMenuConfigurationItem__popover"
                        :active="activeMenu === 'configuration'"
                        :active-item-value="configurationTab"
                        :items="configurationMenuItems"
                        @dropdown-opening="onDropdownOpening('configuration')"
                    />
                </RbacDisplayGuard>
                <RbacDisplayGuard :features="displayAndEmailMenuFeatures" required-any>
                    <SidebarMenuGroup
                        ref="notificationItem"
                        :icon="notificationIcon"
                        label="Notify"
                        popover-class="SidebarMenu__popover SidebarMenuNotificationItem__popover"
                        :active="activeMenu === 'notifications'"
                        :active-item-value="notificationsTab"
                        :items="notificationMenuItems"
                        @dropdown-opening="onDropdownOpening('notification')"
                    />
                </RbacDisplayGuard>
            </div>
        </div>

        <div class="SidebarMenu__footerContainer">
            <img
                v-if="showPoweredBy"
                class="SidebarMenu__poweredByEvident"
                src="@/assets/images/powered_by_evident.svg"
                alt="Powered by Evident"
            >
            <div class="SidebarMenu__links">
                <RouterLink
                    v-if="rpName"
                    class="SidebarMenu__helpContainer"
                    :to="{ name: 'kbHelp', params: { rpId: rpName } }"
                >
                    <EidIcon :icon-src="faQuestionCircle" />
                    <span class="SidebarMenu__helpText">
                        Help
                    </span>
                </RouterLink>
            </div>
        </div>
    </div>
</template>

<style lang="scss">
@import "@/styles/utilities/colormix.function.scss";

@mixin flex-column {
    display: flex;
    flex-direction: column;
}

@mixin flex-column-gap($gap) {
    @include flex-column;
    gap: $gap;
}

.SidebarMenu {
    height: 100vh;
    overflow: auto;
    @include flex-column-gap(16px);
    justify-content: space-between;
    padding: 20px 8px;
    background: linear-gradient(var(--eidSecureBlue), var(--eidQualityBlack));
    color: var(--eidWhite);
    font-size: 12px;
    text-align: center;

    a {
        text-decoration: none;
        color: var(--eidWhite);
    }

    &__menuContainer {
        @include flex-column-gap(12px);

        li {
            list-style: none;
        }
    }

    &__logoContainer {
        display: flex;
        justify-content: center;
        align-items: center;
        margin-bottom: 40px;

        img {
            height: 35px;
            width: auto;
        }
    }

    &__poweredByEvident {
        padding: 8px 4px;
    }

    &__footerContainer {
        display: flex;
        flex-direction: column;
    }

    &__links {
        @include flex-column-gap(16px);
        border-top: 1.5px solid var(--eidSecureBlue-50);
        padding-top: 16px;
    }

    &__helpContainer {
        @include flex-column-gap(4px);
        padding: 8px 4px;
        border-radius: 8.75px;

        &:hover {
            background: transparent-color(var(--eidWhite), 10%);
        }

        &__helpText {
            font-size: 12px;
            font-weight: 500;
            color: var(--eidWhite);
        }

        .EidIcon {
            font-size: 16px;
        }
    }
}

.SidebarMenu__popover {
    width: 200px;
    padding: 8px 4px;
    border-color: var(--eidLightGray-25);

    .NewCustomDropdown__option {
        padding: 12px 16px;
        border-bottom: none;
        border-radius: 8.75px;

        > span {
            font-weight: 400;
        }

        &:hover,
        &--focused {
            background-color: var(--eidQualityBlack-10);
            color: var(--eidQualityBlack);
        }
    }
}
</style>

<script lang="ts">
    import { defineComponent } from 'vue';

    import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
    import { faQuestionCircle } from '@fortawesome/free-regular-svg-icons';
    import { faUsers, faChartTreeMap, faSquareCheck, faGear, faBell, faBellSlash } from '@fortawesome/pro-light-svg-icons';
    import { faSparkles } from '@fortawesome/pro-regular-svg-icons';

    import EidIcon from '@evidentid/dashboard-commons/components/EidIcon/EidIcon.vue';
    import { useUserStore } from '@evidentid/dashboard-commons/modules/auth/store/user';
    import RbacDisplayGuard
        from '@evidentid/dashboard-commons/modules/role-base-access-control/components/rbac-guard/RbacDisplayGuard.vue';
    import { TprmRequestsConfig } from '@evidentid/tprm-portal-lib/models/common/TprmRequestsConfig.model';
    import {
        NotificationConfiguration,
        NotificationType,
        REMINDERLESS_NOTIFICATION_TYPES,
    } from '@evidentid/tprm-portal-lib/models/notification-configuration';
    import { RbacFeature } from '@evidentid/tprm-portal-lib/models/rbac';
    import { OperationStatus } from '@evidentid/vue-commons/store/OperationStatus';

    import { useContractExtractionStore } from '@/modules/contract-extraction/store';
    import { createEmptyRequestsConfigStatus, useDashboardStore } from '@/modules/dashboard/vuex/dashboard';
    import { useEntityActionsReviewStore } from '@/modules/entity-actions-review/store';
    import { useNotificationsConfigurationStore } from '@/modules/notifications-configuration/store';

    import SidebarMenuGroup from './sub-components/SidebarMenuGroup.vue';
    import SidebarMenuItem from './sub-components/SidebarMenuItem.vue';

    interface NotificationConfig {
        fetchCount: (() => void) | null;
        getCount: (() => number) | null;
    }

    interface MenuItem {
        name: string;
        href: string;
        label: string;
        icon: IconDefinition;
        notificationConfig?: NotificationConfig;
        processing?: boolean;
    }

    // The Configure group's own items, each gated by the RBAC feature that guards its page (see the
    // configurationMenuItems computed) — a user only sees the links they can actually open.
    // `requiresGoApp` items also need the RP to have the Go App enabled.
    const CONFIGURATION_MENU_ITEMS: {
        href: string;
        value: string;
        label: string;
        feature: RbacFeature;
        requiresGoApp?: boolean;
    }[] = [
        {
            href: '#/configuration/activity-history',
            value: 'activityHistory',
            label: 'Activity History',
            feature: RbacFeature.configurationActivityHistory,
        },
        {
            href: '#/configuration/api-settings',
            value: 'apiSettings',
            label: 'API Settings',
            feature: RbacFeature.configurationApiSettings,
        },
        {
            href: '#/configuration/go-app',
            value: 'goAppSettings',
            label: 'Go App',
            feature: RbacFeature.configurationGoAppSettings,
            requiresGoApp: true,
        },
        {
            href: '#/configuration/custom-properties',
            value: 'customProperties',
            label: 'Custom Properties',
            feature: RbacFeature.configurationCustomProperties,
        },
        {
            href: '#/configuration/sso',
            value: 'sso',
            label: 'Single sign-on',
            feature: RbacFeature.configurationUsersAndRoles,
        },
        {
            href: '#/configuration/users',
            value: 'usersAndRoles',
            label: 'Users & Roles',
            feature: RbacFeature.configurationUsersAndRoles,
        },
    ];

    export default defineComponent({
        name: 'SidebarMenu',
        components: {
            RbacDisplayGuard,
            EidIcon,
            SidebarMenuGroup,
            SidebarMenuItem,
        },
        props: {
            showPoweredBy: {
                type: Boolean,
                default: false,
            },
        },
        data() {
            return {
                faUsers,
                faChartTreeMap,
                faSquareCheck,
                faQuestionCircle,
                faGear,
                faBell,
                faBellSlash,
                faSparkles,
                displayAndEmailMenuFeatures: [
                    RbacFeature.displayAndEmailBrandingSettings,
                    RbacFeature.displayAndEmailContactInformation,
                    RbacFeature.displayAndEmailContent,
                    RbacFeature.displayAndEmailSettings,
                ],
                notificationMenuItems: [
                    {
                        href: '#/notifications/branding',
                        value: 'branding',
                        label: 'Branding Settings',
                    },
                    {
                        href: '#/notifications/contact',
                        value: 'contact',
                        label: 'Contact Information',
                    },
                    {
                        href: '#/notifications/email-content',
                        value: 'emailContent',
                        label: 'Email Content',
                    },
                    {
                        href: '#/notifications/email-settings',
                        value: 'emailSettings',
                        label: 'Email Settings',
                    },
                ],
            };
        },
        computed: {
            rpName(): string {
                return (this as any).$rp?.current;
            },
            actionReviewNotificationConfig(): NotificationConfig {
                return {
                    fetchCount: this.rpName
                        ? () => useEntityActionsReviewStore().loadActionCount({ rpName: this.rpName })
                        : null,
                    getCount: this.rpName
                        ? () => useEntityActionsReviewStore().actionCount?.[this.rpName]?.data || 0
                        : null,
                };
            },
            hasProcessingContracts(): boolean {
                const pending = useContractExtractionStore().pendingExtractions?.[this.rpName] || [];
                return pending.length > 0;
            },
            goAppEnabled(): boolean {
                return Boolean((this as any).$rp?.currentDetails?.goAppEnabled);
            },
            availableConfigurationMenuItems(): typeof CONFIGURATION_MENU_ITEMS {
                return CONFIGURATION_MENU_ITEMS.filter((item) => !item.requiresGoApp || this.goAppEnabled);
            },
            configMenuFeatures(): RbacFeature[] {
                return this.availableConfigurationMenuItems.map((item) => item.feature);
            },
            // Filters the Configure group down to the items whose feature the user actually holds, so
            // holding just one of them (e.g. Activity History) never surfaces links to the others.
            configurationMenuItems(): typeof CONFIGURATION_MENU_ITEMS {
                const userFeatures = useUserStore().currentRpRbacFeatures || {};
                return this.availableConfigurationMenuItems.filter((item) => userFeatures[item.feature]);
            },
            flatMenuItems(): MenuItem[] {
                return [
                    { name: 'dashboard', href: '#/dashboard', label: 'Entities', icon: faUsers },
                    {
                        name: 'evie',
                        href: this.rpName ? `#/${this.rpName}/evie-chat` : '#/evie-chat',
                        label: 'Evie AI',
                        icon: faSparkles,
                    },
                    { name: 'decisioning', href: '#/decisioning', label: 'Decisioning', icon: faChartTreeMap },
                    {
                        name: 'actions',
                        href: '#/actions',
                        label: 'Actions',
                        icon: faSquareCheck,
                        notificationConfig: this.actionReviewNotificationConfig,
                        processing: this.hasProcessingContracts,
                    },
                ];
            },
            activeMenu(): string | null {
                const routeName = this.$route.name;
                const routePath = this.$route.path;
                if (routeName === 'dashboard') {
                    return 'dashboard';
                }
                if (routeName === 'evieChat') {
                    return 'evie';
                }
                if (routePath.includes('/decisioning')) {
                    return 'decisioning';
                }
                if (routePath.includes('/actions')) {
                    return 'actions';
                }
                if (routePath.includes('/notifications')) {
                    return 'notifications';
                }
                /*
                 * routePath.includes('/admin') due to manage user route is under admin as it's a shared module,
                 * condition included here in case any unexpected usage by manually type in urls with /admin/manage-user
                 */
                if (routePath.includes('/configuration') || routePath.includes('/admin')) {
                    return 'configuration';
                }
                return null;
            },
            configurationTab(): string | null {
                if (this.activeMenu !== 'configuration') {
                    return null;
                }
                const routeName = String(this.$route.name || '');
                if (routeName === 'configurationActivityHistory') {
                    return 'activityHistory';
                } else if (routeName === 'customProperties') {
                    return 'customProperties';
                } else if (routeName.startsWith('manage-users')) {
                    return 'usersAndRoles';
                } else if (routeName.startsWith('apiSettings')) {
                    return 'apiSettings';
                } else if (routeName === 'goAppSettings') {
                    return 'goAppSettings';
                } else if (routeName === 'sso') {
                    return 'sso';
                }
                return null;
            },
            notificationsTab(): string | null {
                if (this.activeMenu !== 'notifications') {
                    return null;
                }
                const routeName = this.$route.name;
                if (routeName === 'notifications-branding-settings') {
                    return 'branding';
                } else if (routeName === 'notifications-support-contact') {
                    return 'contact';
                } else if (routeName === 'notifications-email-settings') {
                    return 'emailSettings';
                } else if (routeName === 'notifications-email-content') {
                    return 'emailContent';
                }
                return null;
            },
            someNotificationsOff(): boolean {
                const emailTemplates: Partial<Record<NotificationType, NotificationConfiguration>> =
                    useNotificationsConfigurationStore().emailTemplates[this.rpName] || {};
                return Object.entries(emailTemplates)
                    .some(([ type, { initEnabled, remindersEnabled } ]) => (
                        // A reminder-less type carries no cadence, so whether its initial
                        // notification is on is the whole story for that row.
                        REMINDERLESS_NOTIFICATION_TYPES.has(type as NotificationType)
                            ? !initEnabled
                            : !initEnabled && !remindersEnabled
                    ));
            },
            requestsConfigStatus(): { status: OperationStatus, data: TprmRequestsConfig | null } {
                return useDashboardStore().requestsConfig[this.rpName]
                    || createEmptyRequestsConfigStatus();
            },
            loadingRequestsConfig(): boolean {
                return this.requestsConfigStatus.status === OperationStatus.loading;
            },
            loadEmailTemplatesStatus(): OperationStatus {
                return useNotificationsConfigurationStore().loadEmailTemplatesStatus[this.rpName]
                    || OperationStatus.uninitialized;
            },
            loadingEmailTemplates(): boolean {
                return this.loadEmailTemplatesStatus === OperationStatus.loading;
            },
            notificationIcon(): IconDefinition {
                return this.someNotificationsOff ? this.faBellSlash : this.faBell;
            },
        },
        watch: {
            rpName: {
                immediate: true,
                handler() {
                    this.actionReviewNotificationConfig.fetchCount?.();
                    this.loadRequestsConfig();
                    this.loadEmailTemplates();
                },
            },
            $route() {
                this.actionReviewNotificationConfig.fetchCount?.();
            },
            hasProcessingContracts(newVal: boolean, oldVal: boolean) {
                if (!newVal && oldVal) {
                    this.actionReviewNotificationConfig.fetchCount?.();
                }
            },
        },
        methods: {
            onDropdownOpening(openingDropdown: 'configuration' | 'notification') {
                // Close all other dropdowns when one is opening
                if (openingDropdown !== 'configuration') {
                    const configItem = this.$refs.configurationItem as any;
                    configItem?.closeDropdown?.();
                }
                if (openingDropdown !== 'notification') {
                    const notificationItem = this.$refs.notificationItem as any;
                    notificationItem?.closeDropdown?.();
                }
            },
            loadRequestsConfig() {
                // The rpName watcher is immediate, so this runs before the router has
                // resolved an :rpId — requesting config for a null RP 404s.
                if (this.rpName && !this.loadingRequestsConfig) {
                    useDashboardStore().loadRequestsConfig({ rpName: this.rpName });
                }
            },
            loadEmailTemplates() {
                if (this.rpName && !this.loadingEmailTemplates) {
                    useNotificationsConfigurationStore().loadEmailTemplates({
                        rpName: this.rpName,
                        silent: true,
                    });
                }
            },
        },
    });
</script>
